/**
 * The palette in the shape it is marked on the plate.
 *
 * Draws the palette block of the marking file - the patches in their
 * colours, for a mode contrast selection the group frames and texts - from
 * the server's layout (POST api/selection/palette/marking/layout, the same
 * computation as the marking file), so the colours on screen sit where they
 * sit on the physical sample. A grid gets its row and column numbers along
 * the edges. Hovering a patch shows the palette hover card.
 *
 * Props:
 *   layout   : the server's layout (patches, frames, texts, palette_mm, ...)
 *   colors   : { [sampleId]: css colour } for the patches
 *   entries  : { [sampleId]: PaletteSampleTooltip entry } for the hover cards
 */
import React from "react";
import { Box, Tooltip } from "@mui/material";
import PaletteSampleTooltip from "./PaletteSampleTooltip";

const MAX_WIDTH_PX = 560;
/** A patch is never drawn larger than this (a two-colour palette stays small). */
const MAX_PATCH_PX = 40;
const MIN_TEXT_PX = 9;
/** Space for the row/column numbers of a grid. */
const AXIS_PX = 18;

/** Distinct coordinates (rounded to 1 µm) in ascending order. */
function axisPositions(values) {
  return [...new Set(values.map((v) => Math.round(v * 1000) / 1000))].sort((a, b) => a - b);
}

export default function PaletteAsMarked({ layout, colors = {}, entries = {} }) {
  if (!layout || !layout.palette_mm || !layout.patches?.length) return null;
  const { palette_mm: palette, patch_size: patchSize } = layout;
  const isGrid = layout.layout !== "groups";
  const axis = isGrid ? AXIS_PX : 0;
  const scale = Math.min((MAX_WIDTH_PX - axis) / palette.width, MAX_PATCH_PX / patchSize);
  const px = (mm) => mm * scale;
  const toX = (mm) => axis + px(mm - palette.x);
  const toY = (mm) => axis + px(mm - palette.y);
  const width = axis + px(palette.width);
  const height = axis + px(palette.height);
  const textPx = layout.text_height_mm ? Math.max(px(layout.text_height_mm), MIN_TEXT_PX) : 0;

  const columns = isGrid ? axisPositions(layout.patches.map((p) => p.x)) : [];
  const rows = isGrid ? axisPositions(layout.patches.map((p) => p.y)) : [];

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <svg
        data-testid="palette-as-marked"
        data-layout={isGrid ? "grid" : "groups"}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: "block", userSelect: "none" }}
      >
        <rect
          x={toX(palette.x)}
          y={toY(palette.y)}
          width={px(palette.width)}
          height={px(palette.height)}
          fill="#fff"
          stroke="rgba(15, 23, 42, 0.25)"
          strokeWidth={1}
        />
        {columns.map((x, index) => (
          <text
            key={`col-${index}`}
            x={toX(x) + px(patchSize) / 2}
            y={AXIS_PX - 5}
            fontSize={11}
            textAnchor="middle"
            fill="#64748b"
          >
            {index + 1}
          </text>
        ))}
        {rows.map((y, index) => (
          <text
            key={`row-${index}`}
            x={AXIS_PX - 5}
            y={toY(y) + px(patchSize) / 2 + 4}
            fontSize={11}
            textAnchor="end"
            fill="#64748b"
          >
            {index + 1}
          </text>
        ))}
        {layout.patches.map((patch, index) => {
          const rect = (
            <rect
              data-testid="palette-marked-patch"
              data-sample-id={patch.sample_id}
              x={toX(patch.x)}
              y={toY(patch.y)}
              width={px(patchSize)}
              height={px(patchSize)}
              fill={colors[patch.sample_id] || "#94a3b8"}
              stroke="rgba(15, 23, 42, 0.2)"
              strokeWidth={0.5}
            />
          );
          const entry = entries[patch.sample_id];
          return entry ? (
            <Tooltip key={`${patch.sample_id}-${index}`} placement="top" title={<PaletteSampleTooltip entry={entry} />}>
              {rect}
            </Tooltip>
          ) : (
            <React.Fragment key={`${patch.sample_id}-${index}`}>{rect}</React.Fragment>
          );
        })}
        {(layout.frames || []).map((frame, index) => (
          <rect
            key={`frame-${index}`}
            x={toX(frame.x)}
            y={toY(frame.y)}
            width={px(frame.width)}
            height={px(frame.height)}
            fill="none"
            stroke="#1f2937"
            strokeWidth={0.75}
          />
        ))}
        {(layout.texts || []).map((text, index) => (
          <text
            key={`text-${index}`}
            x={toX(text.x)}
            y={toY(text.y) + textPx}
            fontSize={textPx}
            fill="#1f2937"
          >
            {text.text}
          </text>
        ))}
      </svg>
    </Box>
  );
}

/** One line on the shape: "5 × 6 grid, filled row by row from the top-left." */
export function paletteShapeText(layout) {
  if (!layout) return "";
  if (layout.layout === "groups") {
    return "The mode contrast groups as they are marked on the palette plate.";
  }
  const grid = layout.grid;
  const shape = grid ? `${grid[0]} × ${grid[1]} grid` : "grid";
  return `As marked on the palette plate: a ${shape}, filled row by row from the top-left corner.`;
}
